import { apiBaseUrl } from '../firebase';
import { fetchWithAuth } from './vpsApi';

export type UploadUrlResponse = {
  uploadUrl: string;
  objectKey: string;
  requiredHeaders?: Record<string, string>;
  expiresInSeconds?: number;
};

export type DownloadUrlResponse = {
  downloadUrl: string;
  expiresInSeconds?: number;
};

export function isMediaObjectKey(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('media/');
}

function requireApiBase(): string {
  const base = (apiBaseUrl || '').replace(/\/$/, '');
  if (!base) throw new Error('api-base-not-configured');
  return base;
}

export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;
export const FILE_TOO_LARGE_MESSAGE = 'This file is larger than 500 MB. Please choose a smaller or compressed file.';

/** Alerts and returns false when the file is over the upload size limit. */
export function checkUploadSize(file: Blob | null | undefined): boolean {
  if (file && file.size > MAX_UPLOAD_BYTES) {
    window.alert(FILE_TOO_LARGE_MESSAGE);
    return false;
  }
  return true;
}

export async function requestUploadUrl(opts: {
  filename: string;
  contentType: string;
  size: number;
}): Promise<UploadUrlResponse> {
  if (opts.size > MAX_UPLOAD_BYTES) throw new Error('file_too_large');
  const base = requireApiBase();
  const res = await fetchWithAuth(`${base}/media/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      filename: opts.filename,
      contentType: opts.contentType,
      size: opts.size,
    }),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (res.status === 413) throw new Error('file_too_large');
  if (!res.ok || !json?.uploadUrl || !json?.objectKey) {
    throw new Error(`media-upload-url-failed:${res.status}:${(json && (json.error || json.code)) || res.statusText}`);
  }

  return {
    uploadUrl: String(json.uploadUrl),
    objectKey: String(json.objectKey),
    requiredHeaders: json.requiredHeaders && typeof json.requiredHeaders === 'object' ? json.requiredHeaders : undefined,
    expiresInSeconds: typeof json.expiresInSeconds === 'number' ? json.expiresInSeconds : undefined,
  };
}

export async function requestDownloadUrl(objectKey: string): Promise<DownloadUrlResponse> {
  const base = requireApiBase();
  const url = `${base}/media/download-url?objectKey=${encodeURIComponent(objectKey)}`;
  const res = await fetchWithAuth(url, { method: 'GET' });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.downloadUrl) {
    throw new Error(`media-download-url-failed:${res.status}:${(json && (json.error || json.code)) || res.statusText}`);
  }
  return {
    downloadUrl: String(json.downloadUrl),
    expiresInSeconds: typeof json.expiresInSeconds === 'number' ? json.expiresInSeconds : undefined,
  };
}

export async function deleteMediaObject(objectKey: string): Promise<void> {
  const base = requireApiBase();
  const res = await fetchWithAuth(`${base}/media/delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ objectKey }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`media-delete-failed:${res.status}:${txt || res.statusText}`);
  }
}

export function uploadFileToSignedUrl(opts: {
  uploadUrl: string;
  file: Blob;
  headers?: Record<string, string>;
  onProgress?: (percent: number) => void;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', opts.uploadUrl, true);

    const headers = opts.headers || {};
    for (const [k, v] of Object.entries(headers)) {
      try {
        xhr.setRequestHeader(k, v);
      } catch {
        // ignore
      }
    }

    xhr.upload.onprogress = (evt) => {
      if (!opts.onProgress) return;
      if (evt.lengthComputable && evt.total > 0) {
        const pct = (evt.loaded / evt.total) * 100;
        opts.onProgress(Math.min(100, Math.max(0, pct)));
      }
    };

    xhr.onerror = () => reject(new Error('r2-upload-xhr-error'));
    xhr.ontimeout = () => reject(new Error('r2-upload-timeout'));

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`r2-upload-failed:${xhr.status}:${xhr.responseText || xhr.statusText}`));
      }
    };

    xhr.send(opts.file);
  });
}

const downloadCache = new Map<string, { url: string; exp: number }>();

export async function getSignedDownloadUrlCached(objectKey: string): Promise<string> {
  const now = Date.now();
  const cached = downloadCache.get(objectKey);
  if (cached && cached.exp > now + 5_000) {
    return cached.url;
  }

  const resp = await requestDownloadUrl(objectKey);
  const ttlMs = (typeof resp.expiresInSeconds === 'number' ? resp.expiresInSeconds : 300) * 1000;
  // Refresh a bit early.
  const exp = now + Math.max(15_000, ttlMs - 20_000);
  downloadCache.set(objectKey, { url: resp.downloadUrl, exp });
  return resp.downloadUrl;
}

/** Returns an already-resolved URL for an object key without a network round trip, if one is cached. */
export function peekCachedMediaUrl(objectKey: string): string | undefined {
  const cached = downloadCache.get(objectKey);
  return cached && cached.exp > Date.now() + 5_000 ? cached.url : undefined;
}

/** Seeds the cache (e.g. with a local blob URL right after upload) so the new media shows instantly. */
export function primeMediaUrl(objectKey: string, url: string, ttlMs = 30 * 60_000) {
  downloadCache.set(objectKey, { url, exp: Date.now() + ttlMs });
}

export async function resolveMediaUrl(urlOrKey: string, opts?: { bypassCache?: boolean }): Promise<string> {
  if (isMediaObjectKey(urlOrKey)) {
    if (opts?.bypassCache) {
      const now = Date.now();
      const resp = await requestDownloadUrl(urlOrKey);
      const ttlMs = (typeof resp.expiresInSeconds === 'number' ? resp.expiresInSeconds : 300) * 1000;
      const exp = now + Math.max(15_000, ttlMs - 20_000);
      downloadCache.set(urlOrKey, { url: resp.downloadUrl, exp });
      return resp.downloadUrl;
    }
    return getSignedDownloadUrlCached(urlOrKey);
  }
  return urlOrKey;
}
